import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import api from '../../services/api';

const SALE_COMPANIES = [
  'Keeneland',
  'Fasig-Tipton',
  'OBS',
  'Inglis',
  'Tattersalls',
  'Goffs',
  'Other'
];

const ALL_DATE_FIELDS = [
  'sale_start_date', 'sale_end_date', 'under_tack_start_date', 'under_tack_end_date',
  'entry_deadline', 'jockey_club_deadline', 'coggins_deadline', 'eva_deadline',
  'withdrawal_deadline', 'health_certificate_deadline', 'stallion_service_cert_deadline',
  'authorized_agent_form_deadline', 'breeding_soundness_exam_deadline',
  'sales_company_contract_deadline', 'pedigree_proof_deadline',
  'pedigree_proof_property_line_deadline', 'first_500_entry_fee_deadline',
  'wish_list_deadline', 'placements_released_deadline', 'request_session_swaps_deadline'
];

const emptyForm = {
  sale_name: '',
  sale_company: '',
  sale_company_other: '',
  sale_currency: 'USD',
  location: '',
  sale_start_date: '',
  sale_end_date: '',
  is_undertack_show: false,
  under_tack_start_date: '',
  under_tack_end_date: '',
  is_digital_sale: false,
  sale_table_link: '',
  sale_type: '',
  session_start_times: [],
  // Universal deadlines
  entry_deadline: '',
  jockey_club_deadline: '',
  coggins_deadline: '',
  eva_deadline: '',
  withdrawal_deadline: '',
  health_certificate_deadline: '',
  // Company-specific deadlines
  stallion_service_cert_deadline: '',
  authorized_agent_form_deadline: '',
  breeding_soundness_exam_deadline: '',
  sales_company_contract_deadline: '',
  pedigree_proof_deadline: '',
  pedigree_proof_property_line_deadline: '',
  first_500_entry_fee_deadline: '',
  wish_list_deadline: '',
  placements_released_deadline: '',
  request_session_swaps_deadline: '',
  is_system_wide: true,
  tenant_id: '',
  notes: ''
};

const DateField = ({ label, fieldKey, formData, onChange, note }) => (
  <div className="space-y-1">
    <Label className="text-sm">{label}{note && <span className="text-xs text-muted-foreground ml-1">({note})</span>}</Label>
    <Input
      type="date"
      value={formData[fieldKey] || undefined}
      onChange={(e) => onChange(fieldKey, e.target.value)}
      className={formData[fieldKey] ? 'date-filled' : ''}
    />
  </div>
);

const CreateSaleDialog = ({ open, onClose, onSuccess, sale }) => {
  const [tenants, setTenants] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (sale) {
      const mapped = { ...emptyForm };
      // Map all fields from sale object
      Object.keys(emptyForm).forEach(key => {
        if (ALL_DATE_FIELDS.includes(key) && sale[key]) {
          mapped[key] = format(new Date(sale[key]), 'yyyy-MM-dd');
        } else if (sale[key] !== undefined) {
          mapped[key] = sale[key];
        }
      });
      // Derive is_undertack_show from existing date fields if not explicitly set
      if (mapped.is_undertack_show === undefined) {
        mapped.is_undertack_show = !!(sale.under_tack_start_date || sale.under_tack_end_date);
      }
      setFormData(mapped);
    } else {
      setFormData(emptyForm);
    }
  }, [sale, open]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sale_name || !formData.sale_start_date || !formData.sale_end_date) {
      toast.error('Please fill in sale name, start date, and end date');
      return;
    }
    if (formData.sale_company === 'Other' && !formData.sale_company_other) {
      toast.error('Please enter company name');
      return;
    }
    if (!formData.is_system_wide && !formData.tenant_id) {
      toast.error('Please select a tenant for tenant-specific sale');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      payload.sale_company = formData.sale_company === 'Other' ? formData.sale_company_other : formData.sale_company;
      ALL_DATE_FIELDS.forEach(field => {
        payload[field] = formData[field] ? formData[field] ? `${formData[field]}T12:00:00.000Z` : null : null;
      });
      payload.tenant_id = formData.is_system_wide ? null : formData.tenant_id;

      if (sale) {
        await api.put(`/sales-management/${sale.id}`, payload);
      } else {
        await api.post('/sales-management', payload);
      }

      onSuccess();
      toast.success('Sale saved successfully');
    } catch (error) {
      console.error('Error saving sale:', error);
      toast.error('Failed to save sale');
    } finally {
      setSaving(false);
    }
  };

  const company = formData.sale_company;
  const isInglis = company === 'Inglis';
  const isFasig = company === 'Fasig-Tipton';
  const isKeeneland = company === 'Keeneland';
  const hasCompanySpecific = isInglis || isFasig || isKeeneland;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sale ? 'Edit' : 'Create'} Sale</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* System-Wide Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded">
            <div>
              <Label>System-Wide Sale</Label>
              <p className="text-xs text-muted-foreground">Available to all tenants</p>
            </div>
            <Switch
              checked={formData.is_system_wide}
              onCheckedChange={(checked) => handleChange('is_system_wide', checked)}
            />
          </div>

          {/* Tenant Selection */}
          {!formData.is_system_wide && (
            <div className="space-y-2">
              <Label>Tenant *</Label>
              <Select value={formData.tenant_id} onValueChange={(v) => handleChange('tenant_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select tenant..." /></SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Sale Name */}
            <div className="col-span-2 space-y-2">
              <Label>Sale Name *</Label>
              <Input value={formData.sale_name} onChange={(e) => handleChange('sale_name', e.target.value)} placeholder="e.g., Keeneland September Yearling Sale" />
            </div>

            {/* Sale Company */}
            <div className="space-y-2">
              <Label>Sale Company *</Label>
              <Select value={formData.sale_company} onValueChange={(v) => handleChange('sale_company', v)}>
                <SelectTrigger><SelectValue placeholder="Select company..." /></SelectTrigger>
                <SelectContent>
                  {SALE_COMPANIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {formData.sale_company === 'Other' && (
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input value={formData.sale_company_other} onChange={(e) => handleChange('sale_company_other', e.target.value)} placeholder="Enter company name" />
              </div>
            )}

            {/* Location */}
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={formData.location} onChange={(e) => handleChange('location', e.target.value)} placeholder="e.g., Lexington, KY" />
            </div>

            {/* Sale Dates */}
            <DateField label="Sale Start Date *" fieldKey="sale_start_date" formData={formData} onChange={handleChange} />
            <DateField label="Sale End Date *" fieldKey="sale_end_date" formData={formData} onChange={handleChange} />

            {/* Undertack Show */}
            <div className="col-span-2 flex items-center justify-between p-3 bg-muted/30 rounded">
              <div>
                <Label className="text-sm font-medium">Undertack Show</Label>
                <p className="text-xs text-muted-foreground">This sale includes an undertack show with timed breezes</p>
              </div>
              <Switch
                checked={!!formData.is_undertack_show}
                onCheckedChange={(checked) => {
                  handleChange('is_undertack_show', checked);
                  if (!checked) {
                    handleChange('under_tack_start_date', '');
                    handleChange('under_tack_end_date', '');
                  }
                }}
              />
            </div>

            {formData.is_undertack_show && (
              <>
                <DateField label="Undertack Start Date" fieldKey="under_tack_start_date" formData={formData} onChange={handleChange} />
                <DateField label="Undertack End Date" fieldKey="under_tack_end_date" formData={formData} onChange={handleChange} />
              </>
            )}

            {/* Digital Sale */}
            <div className="col-span-2 flex items-center justify-between p-3 bg-muted/30 rounded">
              <div>
                <Label className="text-sm font-medium">Digital Sale</Label>
                <p className="text-xs text-muted-foreground">This sale includes a digital / online bidding component (e.g. Inglis Digital)</p>
              </div>
              <Switch
                checked={!!formData.is_digital_sale}
                onCheckedChange={(checked) => handleChange('is_digital_sale', checked)}
              />
            </div>

            {/* Sale Currency */}
            <div className="space-y-2">
              <Label>Sale Currency</Label>
              <Select value={formData.sale_currency} onValueChange={(v) => handleChange('sale_currency', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (&euro;)</SelectItem>
                  <SelectItem value="GBP">GBP (&pound;)</SelectItem>
                  <SelectItem value="CAD">CAD ($)</SelectItem>
                  <SelectItem value="AUD">AUD ($)</SelectItem>
                  <SelectItem value="JPY">JPY (&yen;)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Universal Deadlines */}
            <div className="col-span-2 pt-2 border-t">
              <h3 className="font-semibold text-sm mb-3">Universal Deadlines</h3>
              <div className="grid grid-cols-2 gap-4">
                <DateField label="Entry Deadline" fieldKey="entry_deadline" formData={formData} onChange={handleChange} />
                <DateField label="Jockey Club Paperwork" fieldKey="jockey_club_deadline" formData={formData} onChange={handleChange} />
                <DateField label="Coggins Deadline" fieldKey="coggins_deadline" formData={formData} onChange={handleChange} />
                <DateField label="EVA Deadline" fieldKey="eva_deadline" formData={formData} onChange={handleChange} />
                <DateField label="Withdrawal Deadline" fieldKey="withdrawal_deadline" formData={formData} onChange={handleChange} />
                <DateField label="Health Certificate Deadline" fieldKey="health_certificate_deadline" formData={formData} onChange={handleChange} />
              </div>
            </div>

            {/* Inglis-Specific Deadlines */}
            {isInglis && (
              <div className="col-span-2 pt-2 border-t">
                <h3 className="font-semibold text-sm mb-1">Inglis Deadlines</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DateField label="Stallion Service Certificate" fieldKey="stallion_service_cert_deadline" formData={formData} onChange={handleChange} note="if sold as Broodmare in foal" />
                  <DateField label="Authorized Agent Form" fieldKey="authorized_agent_form_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Breeding Soundness Exam" fieldKey="breeding_soundness_exam_deadline" formData={formData} onChange={handleChange} note="if sold as Broodmare Prospect" />
                </div>
              </div>
            )}

            {/* Fasig-Tipton-Specific Deadlines */}
            {isFasig && (
              <div className="col-span-2 pt-2 border-t">
                <h3 className="font-semibold text-sm mb-1">Fasig-Tipton Deadlines</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DateField label="Sales Company Contract" fieldKey="sales_company_contract_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Pedigree Proof" fieldKey="pedigree_proof_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Stallion Service Certificate" fieldKey="stallion_service_cert_deadline" formData={formData} onChange={handleChange} note="if sold as Broodmare in foal" />
                </div>
              </div>
            )}

            {/* Keeneland-Specific Deadlines */}
            {isKeeneland && (
              <div className="col-span-2 pt-2 border-t">
                <h3 className="font-semibold text-sm mb-1">Keeneland Deadlines</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DateField label="First $500 of Entry Fee" fieldKey="first_500_entry_fee_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Wish List" fieldKey="wish_list_deadline" formData={formData} onChange={handleChange} note="not linked to deliverable" />
                  <DateField label="Authorized Agent Form" fieldKey="authorized_agent_form_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Pedigree Proof" fieldKey="pedigree_proof_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Pedigree Proof - Property Line Changes" fieldKey="pedigree_proof_property_line_deadline" formData={formData} onChange={handleChange} />
                  <DateField label="Placements Released" fieldKey="placements_released_deadline" formData={formData} onChange={handleChange} note="not linked to deliverable" />
                  <DateField label="Request Session Swaps" fieldKey="request_session_swaps_deadline" formData={formData} onChange={handleChange} note="not linked to deliverable" />
                  <DateField label="Stallion Service Certificate" fieldKey="stallion_service_cert_deadline" formData={formData} onChange={handleChange} note="if sold as Broodmare in foal" />
                </div>
              </div>
            )}
          </div>

          {/* Sale Window */}
          <div className="space-y-3 border rounded-lg p-4">
            <h3 className="text-sm font-semibold">Sale Window (Live ETA)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Live Sale Table URL</Label>
                <Input
                  value={formData.sale_table_link || ''}
                  onChange={(e) => handleChange('sale_table_link', e.target.value)}
                  placeholder="e.g. https://www.keeneland.com/sales/.../catalog-table/"
                  className="text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  The public sale catalog/results page. The system scrapes this every 60 seconds during the sale to estimate arrival times.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sale Type</Label>
                <Select value={formData.sale_type || ''} onValueChange={(v) => handleChange('sale_type', v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {['2YO','Mixed','HORA','Yearling','Breeding Stock','Other'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Per-day start times — only shown when a sale table link is set and dates exist */}
            {formData.sale_table_link && formData.sale_start_date && (() => {
              // Generate date range from sale_start_date to sale_end_date
              const dates = [];
              try {
                const start = new Date(formData.sale_start_date.split('T')[0] + 'T12:00:00Z');
                const end   = formData.sale_end_date
                  ? new Date(formData.sale_end_date.split('T')[0] + 'T12:00:00Z')
                  : start;
                let cur = new Date(start);
                while (cur <= end && dates.length < 14) {
                  dates.push(cur.toISOString().split('T')[0]);
                  cur.setDate(cur.getDate() + 1);
                }
              } catch { /* ignore */ }
              if (!dates.length) return null;
              const times = formData.session_start_times || [];
              const getTime = (d) => (times.find(t => t.date === d) || {}).start_time || '10:00';
              const getEntry = (d) => times.find(t => t.date === d) || {};
              const setTime = (d, v) => {
                const existing = times.find(t => t.date === d) || {};
                const updated = times.filter(t => t.date !== d);
                updated.push({ ...existing, date: d, start_time: v });
                handleChange('session_start_times', updated);
              };
              const setHip = (d, field, v) => {
                const existing = times.find(t => t.date === d) || {};
                const updated = times.filter(t => t.date !== d);
                updated.push({ ...existing, date: d, start_time: getTime(d), [field]: v ? parseInt(v) : null });
                handleChange('session_start_times', updated);
              };
              return (
                <div className="space-y-2 mt-2">
                  <Label className="text-xs">Session Start Times &amp; Hip Ranges</Label>
                  <div className="space-y-1.5">
                    {dates.map(d => (
                      <div key={d} className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground w-24 shrink-0">
                          {new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <Input
                          type="time"
                          value={getTime(d)}
                          onChange={e => setTime(d, e.target.value)}
                          className={`h-7 text-xs w-28 ${times.find(t => t.date === d) ? 'time-filled' : ''}`}
                        />
                        <span className="text-[10px] text-muted-foreground">Hips</span>
                        <Input
                          type="number" min="1" placeholder="From"
                          value={getEntry(d).start_hip || ''}
                          onChange={e => setHip(d, 'start_hip', e.target.value)}
                          className="h-7 text-xs w-20"
                        />
                        <span className="text-[10px] text-muted-foreground">–</span>
                        <Input
                          type="number" min="1" placeholder="To"
                          value={getEntry(d).end_hip || ''}
                          onChange={e => setHip(d, 'end_hip', e.target.value)}
                          className="h-7 text-xs w-20"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Start time and hip range used to project ETAs before any horses have sold each day.</p>
                </div>
              );
            })()}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Additional information about this sale..." rows={3} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Sale'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSaleDialog;
